// Two different things are called "username" on the wire — keep them
// apart in code: `name` is the DISPLAY name, `username` is the LOGIN HANDLE
// (2026-10-19). The login response and PATCH /login/user/edit still carry
// the display name under `username` / `new_username` (legacy contract);
// every call site touching those is commented `WIRE:`.

/** Body of POST /login/user/new. */
export type FullUserDetails = {
    UID: Number,
    warehouse: string;
    /** Display name. */
    name: string;
    /** Login handle — 3–15 lowercase letters/digits (helpers/usernameRules). */
    username: string;
    /** Policy password (14+ Manager+, 9+ otherwise). Temporary by default:
     *  the user rotates it at first login unless `mustChangePassword` is false. */
    password: string;
    permissions: string;
    userRFID: string;
    /** Optional contact address — required for Manager/Admin (email login,
     *  login links). Empty string / undefined = no email on the account. */
    email?: string | null;
    mustChangePassword?: boolean;
};

export type displayUserType = {
    "_id": string,
    "UID": number,
    /** Display name. */
    "name": string,
    /** Login handle. Null/absent = not migrated yet — still signs in by
     *  colour + PIN and will be asked to choose one at next login. */
    "username"?: string | null,
    /** Derived by the BE: true when `username` is unset. */
    "needsAccountSetup"?: boolean,
    "permissions": string,
    "warehouse": string,
    /** RFID login tag. Null or absent for anyone who never had a tag assigned. */
    "userRFID": string | null,
    /** Optional contact address — powers email login (Manager+) and the
     *  "email me a login link" flow. Null on accounts without one. */
    "email"?: string | null,
    /** ISO timestamp of the user's last successful login. Null on legacy
     *  docs that pre-date the field. */
    "lastActiveAt"?: string | null,
    /** ISO timestamp of when the password was last set. Null on legacy
     *  docs (treated as "never rotated"). */
    "passwordChangedAt"?: string | null,
    /** "active" | "suspended" — `inactive` (soft-deleted) users don't
     *  reach the FE list. Optional because legacy admin-list responses
     *  pre-date the field and may omit it; default-treat as "active". */
    "status"?: "active" | "suspended" | string,
    /** ISO timestamp of when the user was suspended (manually or by
     *  the auto-suspend job). Null when active. */
    "suspendedAt"?: string | null,
    /** True when an admin has flagged the user to rotate their
     *  password on next login. False once they have. Null/undefined on
     *  legacy docs. */
    "mustChangePassword"?: boolean | null,
    /** ISO timestamp of when the user record was created. New users
     *  get the value at insert; legacy users get backfilled from
     *  their ObjectId timestamp by the BE on first auto-suspend run. */
    "createdAt"?: string | null,
    /** True when this account is exempt from the one-session-per-user
     *  rule — shared kiosk stations and automated clients that need
     *  concurrent logins. Absent/false on every normal user. Only a
     *  superAdmin can change it. */
    "allowConcurrentSessions"?: boolean,
    /** Two-factor authentication (2026-10-20). ISO timestamp of a confirmed
     *  enrolment, null when the user has no authenticator yet. */
    "twoFactorEnrolledAt"?: string | null,
    /** True when this account is asked for a code at sign-in (its position,
     *  or a per-user override) — i.e. the people the "2FA pending" badge
     *  and the Reset two-factor action apply to. */
    "twoFactorRequired"?: boolean,
    /** "position" | "user" | "user-released" | "not-required" */
    "twoFactorReason"?: string,
};

/** True when the account has no login handle yet (still on colour + PIN). */
export const userNeedsAccountSetup = (u: Pick<displayUserType, "username" | "needsAccountSetup">): boolean =>
    u.needsAccountSetup ?? !u.username;
